import {
  Flex,
  Tooltip as ChakraTooltip,
  AvatarGroup,
  Avatar,
} from "@chakra-ui/react";
import VaultHeader from "@components/V2/Vault/Common/VaultHeader";
import { useAgEthVault, useAgEthDeposit, useAgEthWithdraw } from "./agEthHooks";
import VaultDWB from "@components/V2/Vault/Common/VaultDWB";
import Portfolio from "@components/V2/Vault/Common/Portfolio";
import DefiPartnerships from "@components/V2/Vault/Common/DefiPartnerships";
import Tooltip from "@components/Tooltip";
import { Typography } from "@kelp-dao/kernel-design-system";
import { DepositProps } from "@components/V2/Vault/Common/Deposit";
import { WithdrawProps } from "@components/V2/Vault/Common/Withdraw";
import { LSTIconMapping, IS_STAKING_PAUSED } from "@constants/common";
import { AIRDROP_VAULT_STRINGS } from "./constants";
import useAgEthClaims from "./useAgEthClaims";
import { getUrlForLocalImage } from "utils";
import BaseImage from "@components/BaseImage";
import { WAGETH } from "@config/contractsConfig";

const AIRDROP_VAULT_POINTS =
  AIRDROP_VAULT_STRINGS.SECTIONS.PORTFOLIO.POINTS.supported;

const renderRewards = (
  <ChakraTooltip
    bg="#FFFFFF"
    color="#075A5A"
    borderRadius="8px"
    hidden={false}
    boxShadow="0px 4px 24px rgba(7, 90, 90, 0.24)"
    label={
      <Flex direction="column" padding="12px 6px" gap="10px">
        {AIRDROP_VAULT_POINTS.map((data) => (
          <Flex
            key={data.key}
            gap="0.5rem"
            alignItems="center"
            fontSize={{ base: "12px", md: "14px" }}
            fontWeight={400}
          >
            <BaseImage
              src={data.logo}
              alt={data.title}
              width={20}
              height={20}
            />
            {data.title}
          </Flex>
        ))}
      </Flex>
    }
    placement="bottom"
  >
    <AvatarGroup
      zIndex="1"
      size="xs"
      max={4}
      sx={{
        "& > span:first-of-type": {
          bg: "white",
          color: "#075A5A",
          zIndex: 5,
        },
        "& span": {
          mr: 0,
          ml: "-10px",
        },
        "& span:last-of-type": {
          ml: 0,
        },
      }}
    >
      {AIRDROP_VAULT_POINTS.map((data, i) => (
        <Avatar
          key={data.key}
          name={data.title}
          bg={"#FFFFFF"}
          src={getUrlForLocalImage(data.logo)}
          zIndex={i}
        />
      ))}
    </AvatarGroup>
  </ChakraTooltip>
);

const AirdropVault = () => {
  const {
    vaultHeaderData,
    tabsData,
    portfolioConfig,
    isSanctioned,
    constants,
    apyTooltipInfo,
  } = useAgEthVault();

  const depositLogic = useAgEthDeposit();
  const withdrawLogic = useAgEthWithdraw();

  // Convert legacy logic to new simple props
  const depositProps: DepositProps = {
    vaultTokenSymbol: "agETH",
    inputTokenSymbol: (depositLogic as any)?.selectedERC20Vault,
    inputTokenIcon:
      LSTIconMapping[(depositLogic as any)?.selectedERC20Vault || "ETH"] ||
      LSTIconMapping["ETH"],
    vaultTokenIcon: LSTIconMapping[WAGETH],
    inputTokenBalance: (depositLogic as any)?.tokenBalance || 0,
    vaultTokenBalance: (depositLogic as any)?.xTokenVaultBalance || 0,
    convertedAmount: (depositLogic as any)?.convertedAmount || "0",
    exchangeRateDisplay:
      (depositLogic as any)?.exchangeRateDisplay ||
      `1 agETH = ${(depositLogic as any)?.valueInEth || "-"} ETH`,
    isConnected: (depositLogic as any)?.isConnected || false,
    isApproved: (depositLogic as any)?.isApproved || false,
    isSupportedChain: (depositLogic as any)?.isVaultSupportedChain !== false,
    onDeposit: (amount?: string) =>
      (depositLogic as any)?.handleClick?.(amount),
    onApprove: (amount?: string) =>
      (depositLogic as any)?.handleClick?.(amount),
    onAmountChange: (depositLogic as any)?.handleChange,
    onTokenSelect: (depositLogic as any)?.handleTokenSelect,
    checkInputConstraints: (depositLogic as any)?.checkInput,
    vaultType: "airdrop",
    disclaimer: <></>,
    disabled: IS_STAKING_PAUSED,
  };

  const withdrawProps: WithdrawProps = {
    vaultTokenSymbol: "agETH",
    outputTokenSymbol: "rsETH",
    vaultTokenIcon: LSTIconMapping[WAGETH],
    outputTokenIcon: LSTIconMapping["rsETH"],
    vaultTokenBalance: (withdrawLogic as any)?.tokenBalance || 0,
    outputTokenBalance: (withdrawLogic as any)?.xTokenTokenBalance || 0,
    convertedAmount: (withdrawLogic as any)?.convertedAmount || "0",
    exchangeRateDisplay:
      (withdrawLogic as any)?.exchangeRateDisplay ||
      `1 agETH = ${(withdrawLogic as any)?.valueInEth || "-"} ETH`,
    isConnected: (withdrawLogic as any)?.isConnected || false,
    isApproved: true, // No approval step in agETH withdraw
    isSupportedChain: (withdrawLogic as any)?.isVaultSupportedChain !== false,
    onWithdraw: (amount?: string) =>
      (withdrawLogic as any)?.handleClick?.(amount),
    onAmountChange: (withdrawLogic as any)?.handleChange,
    checkInputConstraints: (withdrawLogic as any)?.checkInput,
    vaultType: "airdrop",
    disclaimer: (
      <>
        The withdrawal amount will be <strong>auto-claimed</strong> to your
        address in 5-6 days once you request a withdrawal. Check pending claims
        in the ‘Claims’ section. While in the withdrawal queue, you will not
        earn any position rewards or points. All your withdrawals in a day will
        be fulfilled as a single transaction.
      </>
    ),
  };

  // Get bridge redirect URL from config, default to /bridge-wrap-ageth
  const bridgeRedirectUrl = "/bridge-wrap-ageth";

  // Determine available tabs based on what's configured
  const availableTabs = tabsData?.map(
    (tab: { key: string; title: string }) =>
      tab.key as "deposit" | "withdraw" | "bridge" | "claim"
  ) || ["deposit", "withdraw"];

  return (
    <>
      <Flex
        direction={"column"}
        alignItems={"center"}
        gap="20px"
        // p="16px"
        maxW="1279px"
        w="100%"
        mx="auto"
        pointerEvents={isSanctioned ? "none" : "auto"}
        opacity={isSanctioned ? 0.5 : 1}
      >
        <VaultHeader
          isLoadingAPYData={vaultHeaderData.isLoadingAPYData}
          isLoadingTotalStaked={vaultHeaderData.isLoadingTotalStaked}
          totalStaked={vaultHeaderData.totalStaked}
          totalStakedEth={vaultHeaderData.totalStakedEth}
          apyValue={vaultHeaderData.apyValue}
          apyLabel={vaultHeaderData.apyLabel}
          totalStakedLabel={vaultHeaderData.totalStakedLabel}
          title={vaultHeaderData.title}
          logo={vaultHeaderData.logo}
          tooltipComponent={
            <Tooltip
              placement="top"
              label={
                <div>
                  <Typography type="body" variant="xsmall">
                    {apyTooltipInfo?.includes(" | ")
                      ? apyTooltipInfo.split(" | ")[0]
                      : apyTooltipInfo}
                  </Typography>
                  <Typography type="body" variant="xsmall">
                    Additional Airdrops - Kelp Miles, Infra Points
                  </Typography>
                  {apyTooltipInfo?.includes(" | ") && (
                    <Typography
                      type="body"
                      variant="xsmall"
                      className="font-normal"
                    >
                      Check <strong>&apos;Strategy Reports&apos;</strong> for
                      details
                    </Typography>
                  )}
                </div>
              }
            />
          }
          renderRewards={renderRewards}
          auditedUrl={vaultHeaderData.auditedUrl}
        />

        <VaultDWB
          availableTabs={availableTabs}
          tabLabels={{
            deposit: "Deposit",
            withdraw: "Withdraw",
            bridge: "Bridge",
          }}
          initialTab="deposit"
          depositProps={depositProps}
          withdrawProps={withdrawProps}
          bridgeRedirectUrl={
            availableTabs.includes("bridge") ? bridgeRedirectUrl : undefined
          }
        />

        <Portfolio
          config={portfolioConfig}
          constants={constants}
          vaultType="airdrop"
          xTokenSymbol="agETH"
          asset="ageth"
          useClaimsHook={useAgEthClaims}
          aprTooltip="Realised APR post fee"
        />

        <DefiPartnerships asset="ageth" />
      </Flex>
    </>
  );
};

export default AirdropVault;
